/**
 * Morse — der Abspieler der Hörspur. (07.10.2026)
 *
 * Martin: „Bitte, wenn geht, so funktioniert die Übung auch mit einem Abspielbutton, dass man sich anhören kann,
 * wie alles funktioniert. Also eine Audio-Spur zum Anhören.“
 *
 * EIN Abspieler für beide Listen (morse-hoerspur-liste.ts: Vorführung, Tabelle der Reihe nach). Er kennt keine
 * Morse-Regeln und kein DOM: er geht die Schritte durch, spielt jeden Ton über den EINEN `MorseSpieler`
 * (morse-ton.ts — der geteilte AudioContext, `resume()` wird dort abgewartet, Beginn und Ende gehen dort an den
 * Seiten-Ton-Melder) und meldet jeden Schritt an die Anzeige (morse-hoerspur-ansicht.ts).
 *
 * Ton-Regeln (jede war schon einmal ein Vorfall — morse-ton.ts, tts-pcm.ts, seiten-ton.ts):
 *   - Der Kontext wird in der Geste geweckt (`wachHalten`, ohne `await` davor) und bleibt für die ganze Spur wach;
 *     beim Ende, Stopp und in der Pause lässt der Abspieler ihn los — dann schläft er in der App nach 1,5 s (sonst
 *     verliert das Wach-Wort die Tonsitzung).
 *   - Die Tonsitzung (`navigator.audioSession`) steht für die ganze Spur auf `playback` und danach wieder wie vorher
 *     (`sitzungHalten`/`sitzungLoslassen` aus tts-pcm.ts, dem EINEN Besitzer) — auch in den Pausen zwischen den
 *     Schritten, damit sie nicht alle paar Sekunden hin- und herspringt.
 *   - Beginn und Ende der ganzen Spur gehen an den Seiten-Ton-Melder (eine Miete), damit der Mikrofon-Motor der App
 *     in den Lesepausen nicht neu anläuft; jeder Ton meldet zusätzlich selbst (morse-ton.ts).
 *
 * Halten: jeder Weg, der Stille braucht, ruft `stopp(grund)` (die Seite: Reiterwechsel, andere Ansicht, Seite im
 * Hintergrund, Start einer Übung, Antippen eines Zeichens, Sprechtaste). Dazu gilt ein Zähler (`token`): jede
 * Fortsetzung — Ton zu Ende, Uhr abgelaufen — gilt nur, solange er steht; ein Halt macht alle ungültig. So hängt
 * nach einem Halt keine Uhr und kein Ton mehr nach.
 *
 * Pause hält an der Stelle: „Weiter“ spielt den unterbrochenen SCHRITT noch einmal von vorn (ein Schritt dauert
 * Sekunden), nie das ganze Kapitel.
 *
 * Diagnose: Zeilen „[morse] …“ (`spur`) für Start, Pause, Weiter, Ende und Stopp mit Grund, Liste und Dauer — nie
 * der Wortlaut der Texte.
 *
 * Aufnahmen (07.10.2026, Martin mit Bild: „Der Button geht nicht.“ — die Tagesaufnahme lief seit einer Stunde):
 *   - Die SPRECHTASTE nimmt in der Seite auf: dann spielt die Spur nicht (Start, Weiter und jeder Schritt prüfen es).
 *   - Die TAGESAUFNAHME hält für die Spur weich an (`halt`, morse-spur-halt.ts): angehalten wird vor dem ersten Ton —
 *     der Lauf wartet darauf —, fortgesetzt bei JEDEM Ende und in der Pause, genau einmal (`beende` und `pause` sind
 *     die einzigen Ausgänge). Lässt sie sich nicht anhalten, spielt die Spur nicht und sagt es in Worten.
 */
import type { MorseSpieler } from "./morse-ton.js";
import type { HoerListe, HoerSchritt } from "./morse-hoerspur-liste.js";
import type { HaltErgebnis, SpurHalt } from "./morse-spur-halt.js";
import { naechsteSeitenTonId, seitenTonMelder } from "./seiten-ton.js";
import { sitzungHalten, sitzungLoslassen } from "./tts-pcm.js";
import type { ZeitPlan } from "../../shared/morse.js";

type SpurZustand = "bereit" | "spielt" | "pause";

/** Was der Abspieler der Anzeige sagt. */
export type SpurEreignis =
  /** Zustand, Liste, Kapitel oder Schritt haben sich geändert — die Anzeige liest den Abspieler neu. */
  | { art: "zustand" }
  /** Ein Schritt beginnt (Untertitel, Bilder, Fortschritt). */
  | { art: "schritt"; kapitel: number; schritt: number }
  /** Der Ton eines Schritts liegt auf der Uhr; der erste Ton beginnt in `inMs` (für ein Bild, das mitleuchtet). */
  | { art: "ton"; schritt: HoerSchritt; plan: ZeitPlan; inMs: number }
  /** Aus. `fertig`: bis zum Ende gespielt. `meldung`: ein Satz für den Menschen (Fehler, Aufnahme), sonst leer. */
  | { art: "ende"; grund: string; fertig: boolean; besitzer: unknown; liste: HoerListe | null; meldung: string };

interface SpurAbhaengigkeiten {
  spieler: Pick<MorseSpieler, "spiele" | "stopp" | "wachHalten" | "zustand">;
  /** Diagnosezeile (ohne „[morse] “). */
  spur: (text: string) => void;
  /** Zeit in ms (für die Dauer in der Diagnose). */
  jetzt: () => number;
  /** Nimmt die Sprechtaste gerade in der Seite auf? Dann spielt die Spur nicht. */
  sprechtaste: () => boolean;
  /** Der Satz, wenn kein Ton kam (morse-teile.ts `keinTonText`). */
  keinTon: (zustand: string) => string;
  /** Der Satz, wenn die Sprechtaste aufnimmt — je Liste (er nennt den Knopf, auf den man danach noch einmal tippt). */
  sprechtasteText: (liste: HoerListe["id"]) => string;
  /** Die Tagesaufnahme für die Spur weich anhalten (morse-spur-halt.ts). Ohne: keine Tagesaufnahme im Spiel. */
  halt?: SpurHalt;
  /** Der Satz, wenn die Tagesaufnahme sich nicht anhalten ließ oder die Notbremse des Halts griff — je Liste. */
  haltText: (art: HaltText, liste: HoerListe["id"]) => string;
}

/** Wofür es einen Satz zur Tagesaufnahme gibt: ließ sich nicht anhalten (Browser, Frist, Fehler), Notbremse, lief wieder. */
type HaltText = "nimmt-auf" | "frist" | "fehler" | "zeitgrenze" | "laeuft-wieder";

export class MorseHoerspur {
  private _zustand: SpurZustand = "bereit";
  private _liste: HoerListe | null = null;
  private _besitzer: unknown = null;
  private _kapitel = 0;
  private _schritt = 0;
  private token = 0;
  private uhr: ReturnType<typeof setTimeout> | null = null;
  private uhrFertig: (() => void) | null = null;
  private halter: { los: () => void; kennung: string } | null = null;
  private hoerer = new Set<(e: SpurEreignis) => void>();
  private seit: number | null = null;
  private gespieltMs = 0;
  /** Die Antwort auf das Anhalten der Tagesaufnahme — der Lauf wartet darauf vor dem ersten Ton. */
  private haltWarten: Promise<HaltErgebnis> | null = null;

  constructor(private readonly d: SpurAbhaengigkeiten) {
    d.halt?.verbinde({
      beiWechsel: () => this.melde({ art: "zustand" }),
      beiNotbremse: () => this.notbremse(),
    });
  }

  get zustand(): SpurZustand {
    return this._zustand;
  }
  /** Läuft oder pausiert sie (aber nicht: aus)? */
  get aktiv(): boolean {
    return this._zustand !== "bereit";
  }
  get spielt(): boolean {
    return this._zustand === "spielt";
  }
  get pausiert(): boolean {
    return this._zustand === "pause";
  }
  get liste(): HoerListe | null {
    return this._liste;
  }
  /** Wer sie gestartet hat — die Anzeige, die ihre Karte zeigt (eine Kennung, nichts Inhaltliches). */
  get besitzer(): unknown {
    return this._besitzer;
  }
  get kapitel(): number {
    return this._kapitel;
  }
  get schritt(): number {
    return this._schritt;
  }
  /** Ist die Tagesaufnahme gerade für die Spur angehalten (oder wird es gleich)? Für die Karte. */
  get tagesaufnahmeAngehalten(): boolean {
    return this.d.halt?.angehalten === true;
  }
  /** Der Schritt, der gerade dran ist (oder `null`). */
  get aktuellerSchritt(): HoerSchritt | null {
    return this._liste?.kapitel[this._kapitel]?.schritte[this._schritt] ?? null;
  }
  /** Wie weit: der Beginn des Schritts, der dran ist, und die ganze Dauer (ms). */
  fortschritt(): { ms: number; gesamt: number } {
    const l = this._liste;
    if (!l) return { ms: 0, gesamt: 0 };
    return { ms: this.aktuellerSchritt?.ab ?? l.ms, gesamt: l.ms };
  }

  hoere(f: (e: SpurEreignis) => void): () => void {
    this.hoerer.add(f);
    return () => this.hoerer.delete(f);
  }

  private melde(e: SpurEreignis): void {
    for (const f of [...this.hoerer]) {
      try {
        f(e);
      } catch {
        /* eine Anzeige ist nie wichtiger als der Ton */
      }
    }
  }

  /* ── Bedienung ──────────────────────────────────────────────────── */

  /**
   * Eine Liste abspielen — aus einer Geste heraus (ein Klick): der Kontext wird sofort geweckt. Dieselbe Liste
   * läuft schon: nichts (KEIN Doppelstart). Eine andere läuft: sie wird abgelöst. Gibt zurück, ob gestartet wurde.
   */
  starte(liste: HoerListe, o: { besitzer?: unknown; kapitel?: number } = {}): boolean {
    if (this.aktiv) {
      if (this._liste?.id === liste.id && this._besitzer === (o.besitzer ?? null)) return false;
      this.stopp("abgeloest");
    }
    if (liste.schritte === 0) return false;
    if (this.d.sprechtaste()) {
      this.d.spur(`kein-start liste=${liste.id} grund=sprechtaste`);
      this.melde({ art: "ende", grund: "sprechtaste", fertig: false, besitzer: o.besitzer ?? null, liste, meldung: this.d.sprechtasteText(liste.id) });
      return false;
    }
    this._liste = liste;
    this._besitzer = o.besitzer ?? null;
    this._kapitel = Math.min(liste.kapitel.length - 1, Math.max(0, o.kapitel ?? 0));
    this._schritt = 0;
    this._zustand = "spielt";
    this.gespieltMs = 0;
    this.seit = this.d.jetzt();
    this.halteTon();
    this.haltWarten = this.halteAufnahme("start");
    this.d.spur(
      `start liste=${liste.id} kapitel=${this._kapitel + 1}/${liste.kapitel.length} schritte=${liste.schritte} dauer=${Math.round(liste.ms / 1000)}s ton=${this.d.spieler.zustand()}`,
    );
    this.melde({ art: "zustand" });
    void this.lauf(++this.token);
    return true;
  }

  /** Pause: der Ton hält sofort an, der Schritt bleibt der aktuelle. */
  pause(grund = "knopf"): void {
    if (this._zustand !== "spielt") return;
    this.haltAn();
    this._zustand = "pause";
    this.zaehleZeit();
    this.lasseTon();
    // Eine Pause kann lange dauern: die Tagesaufnahme läuft nach dem Nachlauf weiter, „Weiter“ hält sie neu an.
    this.d.halt?.lasse(`liste=${this._liste?.id} anlass=pause`);
    this.d.spur(`pause liste=${this._liste?.id} grund=${grund} kapitel=${this._kapitel + 1} schritt=${this._schritt + 1}`);
    this.melde({ art: "zustand" });
  }

  /** Weiter nach der Pause: der unterbrochene Schritt noch einmal von vorn. Aus einer Geste (Klick). */
  weiter(): void {
    if (this._zustand !== "pause" || !this._liste) return;
    if (this.d.sprechtaste()) {
      // Der Tipp auf „Weiter“ geht ins Leere: es sagt, warum (nie stilles Nichts).
      this.haltAn();
      this.beende("sprechtaste", false, this.d.sprechtasteText(this._liste.id));
      return;
    }
    this._zustand = "spielt";
    this.seit = this.d.jetzt();
    this.halteTon();
    this.haltWarten = this.halteAufnahme("weiter");
    this.d.spur(`weiter liste=${this._liste.id} kapitel=${this._kapitel + 1} schritt=${this._schritt + 1} ton=${this.d.spieler.zustand()}`);
    this.melde({ art: "zustand" });
    void this.lauf(++this.token);
  }

  /** Abspielen/Pause mit EINEM Knopf. */
  umschalten(): void {
    if (this._zustand === "spielt") this.pause("knopf");
    else if (this._zustand === "pause") this.weiter();
  }

  /**
   * Alles aus: kein Ton, kein geplanter Ton, keine Uhr. `tonAus`: auch den Ton des Spielers anhalten — nicht, wenn
   * ein anderer den Spieler schon übernommen hat (dann stoppte das SEINEN Ton).
   */
  stopp(grund: string, o: { tonAus?: boolean; meldung?: string } = {}): void {
    if (this._zustand === "bereit") return;
    this.haltAn(o.tonAus !== false);
    this.beende(grund, false, o.meldung ?? "");
  }

  /**
   * Die Tagesaufnahme nimmt (wieder) auf, während die Spur spielt — von außen fortgesetzt, neu begonnen, oder die
   * Notbremse von native-recorder.ts: die Spur endet, und die Karte sagt es (nie stilles Nichts).
   */
  stoppWeilAufnahme(): void {
    const l = this._liste;
    if (this._zustand === "bereit" || !l) return;
    this.stopp("aufnahme", { meldung: this.d.haltText("laeuft-wieder", l.id) });
  }

  /** Ein Kapitel weiter (`1`) oder zurück (`-1`; mitten im Kapitel: an seinen Anfang). Nur bei mehreren Kapiteln. */
  springe(delta: number): void {
    const l = this._liste;
    if (!l || this._zustand === "bereit") return;
    const ziel = delta < 0 ? (this._schritt > 0 ? this._kapitel : this._kapitel - 1) : this._kapitel + 1;
    const k = Math.min(l.kapitel.length - 1, Math.max(0, ziel));
    if (delta > 0 && k === this._kapitel) return;
    const lief = this._zustand === "spielt";
    if (lief) this.haltAn();
    this._kapitel = k;
    this._schritt = 0;
    this.d.spur(`kapitel liste=${l.id} kapitel=${k + 1}/${l.kapitel.length}`);
    this.melde({ art: "zustand" });
    this.melde({ art: "schritt", kapitel: this._kapitel, schritt: this._schritt });
    if (!lief) return;
    // Zurück verlängert den Rest der Spur: die Notbremse des Halts gilt ab hier neu.
    this.haltWarten = this.halteAufnahme("kapitel");
    void this.lauf(++this.token);
  }

  /* ── Ablauf ─────────────────────────────────────────────────────── */

  /**
   * Der Lauf — nie ein unbehandeltes Versprechen (er wird mit `void` gestartet). Wirft ein Schritt (der Spieler, ein
   * Zeitplan), und der Lauf gilt noch: die Spur endet sauber — Ton aus, Kontext, Sitzung und Melder losgelassen, die
   * Karte sagt es in Worten (07.10.2026, Prüfbericht K2: vorher blieb der Zustand „spielt“, der Melder bekam einen
   * Beginn ohne Ende, und das Versprechen wurde unbehandelt verworfen).
   */
  private async lauf(tok: number): Promise<void> {
    try {
      await this.laufSchritte(tok);
    } catch {
      if (tok !== this.token) return;
      try {
        const liste = this._liste;
        this.d.spur(`fehler liste=${liste?.id} kapitel=${this._kapitel + 1} schritt=${this._schritt + 1}`);
        this.haltAn();
        let zustand = "fehlt";
        try {
          zustand = this.d.spieler.zustand();
        } catch {
          /* ohne Zustand: „fehlt“ */
        }
        this.beende("fehler", false, this.d.keinTon(zustand));
      } catch {
        /* der Lauf ist vorbei; mehr als aufräumen geht nicht, und ein Versprechen darf nicht scheitern */
      }
    }
  }

  private async laufSchritte(tok: number): Promise<void> {
    const liste = this._liste;
    if (!liste) return;
    // Vor dem ersten Ton: die Tagesaufnahme ist angehalten (oder es lief keine). Ließ sie sich nicht anhalten, spielt
    // die Spur nicht — und sagt es.
    const halt = this.haltWarten;
    if (halt) {
      const e = await halt;
      if (tok !== this.token) return;
      if (e === "nimmt-auf" || e === "frist" || e === "fehler") {
        // Die Zeile „stopp … grund=tagesaufnahme-…“ schreibt `beende`.
        this.haltAn();
        this.beende(`tagesaufnahme-${e}`, false, this.d.haltText(e, liste.id));
        return;
      }
    }
    while (tok === this.token) {
      const kap = liste.kapitel[this._kapitel];
      if (!kap) {
        this.beende("ende", true, "");
        return;
      }
      const s = kap.schritte[this._schritt];
      if (!s) {
        this._kapitel++;
        this._schritt = 0;
        continue;
      }
      // Die Sprechtaste hat begonnen, seit der letzte Schritt lief (sie meldet sich hier nicht von selbst).
      if (this.d.sprechtaste()) {
        this.stopp("sprechtaste");
        return;
      }
      this.melde({ art: "schritt", kapitel: this._kapitel, schritt: this._schritt });
      if (s.plan) {
        const plan = s.plan;
        const e = await this.d.spieler.spiele(plan, {
          ton: liste.ton,
          beimStart: (inMs) => this.melde({ art: "ton", schritt: s, plan, inMs }),
        });
        if (tok !== this.token) return;
        if (e === "kein-ton") {
          const zustand = this.d.spieler.zustand();
          this.d.spur(`kein-ton liste=${liste.id} zustand=${zustand} kapitel=${this._kapitel + 1} schritt=${this._schritt + 1}`);
          this.haltAn(false);
          this.beende("kein-ton", false, this.d.keinTon(zustand));
          return;
        }
        // „abgebrochen“ bei gültigem Zähler: ein anderer hat den Spieler genommen — nicht dagegen anspielen.
        if (e === "abgebrochen") {
          this.stopp("uebernommen", { tonAus: false });
          return;
        }
      }
      await this.warte(s.nachMs);
      if (tok !== this.token) return;
      this._schritt++;
    }
  }

  private warte(ms: number): Promise<void> {
    return new Promise((ok) => {
      this.uhrFertig = ok;
      this.uhr = setTimeout(() => {
        this.uhr = null;
        this.uhrFertig = null;
        ok();
      }, Math.max(0, ms));
    });
  }

  /** Die Uhr aus — und das Warten darauf löst auf, damit nichts hängt (die Fortsetzung prüft den Zähler). */
  private uhrAus(): void {
    if (this.uhr) clearTimeout(this.uhr);
    this.uhr = null;
    const ok = this.uhrFertig;
    this.uhrFertig = null;
    ok?.();
  }

  /** Jede laufende Fortsetzung ungültig machen: Zähler hoch, Uhr aus, (eigener) Ton aus. */
  private haltAn(tonAus = true): void {
    this.token++;
    this.uhrAus();
    if (!tonAus) return;
    try {
      this.d.spieler.stopp();
    } catch {
      /* schon still */
    }
  }

  /** Die Tagesaufnahme für die Spur anhalten (falls eine läuft) — die Notbremse: der Rest der Spur ab hier. */
  private halteAufnahme(anlass: string): Promise<HaltErgebnis> | null {
    const l = this._liste;
    if (!this.d.halt || !l) return null;
    return this.d.halt.halte(`liste=${l.id} anlass=${anlass}`, Math.max(0, l.ms - (this.aktuellerSchritt?.ab ?? 0)));
  }

  /** Die Notbremse des Halts hat die Tagesaufnahme fortgesetzt: die Spur endet (über einer laufenden Aufnahme spielt sie nicht). */
  private notbremse(): void {
    const l = this._liste;
    if (this._zustand === "bereit" || !l) return;
    this.haltAn();
    this.beende("zeitgrenze", false, this.d.haltText("zeitgrenze", l.id));
  }

  private zaehleZeit(): void {
    if (this.seit !== null) this.gespieltMs += Math.max(0, this.d.jetzt() - this.seit);
    this.seit = null;
  }

  private beende(grund: string, fertig: boolean, meldung: string): void {
    const liste = this._liste;
    const besitzer = this._besitzer;
    const kapitel = this._kapitel;
    this.zaehleZeit();
    this.lasseTon();
    // Jedes Ende geht hier durch: die Tagesaufnahme läuft weiter — genau einmal, nach dem Nachlauf (morse-spur-halt.ts).
    // Geht die Seite (Hintergrund, pagehide, zerstört), sagt die Seite dem Halt „sofort“ (morse-view.ts, `ruhe`).
    this.haltWarten = null;
    this.d.halt?.lasse(`liste=${liste?.id} anlass=${grund}`);
    this._zustand = "bereit";
    this._liste = null;
    this._besitzer = null;
    this._kapitel = 0;
    this._schritt = 0;
    if (liste) {
      this.d.spur(
        `${fertig ? "ende" : "stopp"} liste=${liste.id} grund=${grund} kapitel=${Math.min(kapitel + 1, liste.kapitel.length)}/${liste.kapitel.length} gespielt=${Math.round(this.gespieltMs / 1000)}s ton=${this.d.spieler.zustand()}`,
      );
    }
    this.melde({ art: "zustand" });
    this.melde({ art: "ende", grund, fertig, besitzer, liste, meldung });
  }

  /* ── Ton: Kontext, Sitzung, Melder — für die ganze Spur ──────────── */

  private halteTon(): void {
    if (this.halter) return;
    // Zuerst wecken, NOCH in der Geste: kein `await` davor.
    const wach = this.d.spieler.wachHalten();
    sitzungHalten();
    const kennung = naechsteSeitenTonId();
    seitenTonMelder.ttsStart(undefined, kennung);
    this.halter = { los: wach.los, kennung };
  }

  private lasseTon(): void {
    const h = this.halter;
    if (!h) return;
    this.halter = null;
    seitenTonMelder.ttsEnde(h.kennung);
    sitzungLoslassen();
    h.los();
  }
}
